import { useState } from 'react';
import { Zap, List } from 'react-feather';
import { twMerge } from 'tailwind-merge';
import { CollectionTriggersTab } from './CollectionTriggersTab';
import { CollectionSequencesTab } from './CollectionSequencesTab';

interface CollectionTriggersAndSequencesPageProps {
  supplierId: string;
}

// Collection Triggers (a condition + a target customer list, enrolling matching customers into a
// sequence) and Collection Sequences (the ordered steps that run once a customer is enrolled -
// directly, or via a trigger) are independently manageable, but live under one page/nav entry with
// two tabs rather than two separate nav items. Sequences leads (both the tab order and the default
// tab) since a Sequence is the thing you'd naturally create first - a Trigger just points at one.
// The page title itself is rendered by DashboardPage's shared top bar (matches `/collectionSequencesAndTriggers`),
// not here - this component only owns the tab switcher and its content.
export const CollectionTriggersAndSequencesPage = ({
  supplierId,
}: CollectionTriggersAndSequencesPageProps) => {
  const [activeTab, setActiveTab] = useState<'SEQUENCES' | 'TRIGGERS'>('SEQUENCES');

  const tabs = [
    { id: 'SEQUENCES' as const, label: 'Collection Sequences', icon: <List size={14} /> },
    { id: 'TRIGGERS' as const, label: 'Collection Triggers', icon: <Zap size={14} /> },
  ];

  return (
    <div className="w-full h-full pt-4">
      <div className="flex gap-1 p-1 rounded-xl bg-gray-200 max-w-md">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={twMerge(
              'flex-1 flex items-center justify-center gap-1.5 h-[34px] text-xs font-medium rounded-lg border-none cursor-pointer transition-all',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'bg-transparent text-gray-500 hover:text-gray-800'
            )}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
      <div className="w-full h-[calc(100vh-130px)]">
        {activeTab === 'SEQUENCES' ? (
          <CollectionSequencesTab supplierId={supplierId} />
        ) : (
          <CollectionTriggersTab supplierId={supplierId} />
        )}
      </div>
    </div>
  );
};

export default CollectionTriggersAndSequencesPage;
